import type { Auftrag } from '@awf/shared';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useAngemeldeterNutzer } from '../auth/auth-context.js';
import { AUFTRAGSTYPEN, hatFreigabeSeite, type TypBeschreibung } from '../auftragstypen.js';
import { FELDSAETZE } from '../forms/field-sets.js';
import { alsDatum, alsUhrzeit } from '../format.js';
import { FinalizationSpinner } from '../ui/lade-animation/finalization-spinner.js';
import { ImageSpinner } from '../ui/lade-animation/image-spinner.js';
import { RevisionSpinner } from '../ui/lade-animation/revision-spinner.js';
import { HinweisBereich, type HinweisDaten } from '../ui/hinweis.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { Phasenleiste } from '../ui/phasenleiste.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import { ErrorState, LoadingState } from '../ui/states.js';
import { Statusmarke, TON_JE_GRUPPE } from '../ui/statusmarke.js';
import { statusText } from '../ui/status-chip.js';
import { AnalyseSeitenListe } from './analyse-seiten-liste.js';
import { BefundKarte } from './befund-karte.js';
import { BriefingKarte } from './briefing-karte.js';
import { angelegtVon, istFremd, NUR_ANSICHT_TEXT } from './einsicht.js';
import { HINWEIS_VERWORFEN_TEXT } from './entscheidung-spalte.js';
import { type Phase, phasenZustaende } from './phasen.js';
import stile from './run-detail-page.module.css';
import type { AuftragsQuelle } from './runs-source.js';
import { aktuellerSchritt, gruppeVon, istHaengend, istVerworfen, STATUS_TEXT } from './status.js';
import { zurAnzeige } from './uebergabe.js';
import { useAuftrag } from './use-runs.js';
import type { StarteVolleAnalyse } from './volle-analyse-knopf.js';

/**
 * Die Fortschrittsseite in der Anatomie der Content-Factory (auftrag-detail/auftrag-detail.tsx,
 * Auftrag 2026-10-13): Seitenkopf, eine Zeile mit Anlage und Gesamtstatus, eine Zeile "Zuletzt
 * nachgesehen" mit Aktualisieren-Knopf, solange etwas laeuft der Hinweis auf die Arbeit im
 * Hintergrund, darunter das Briefing und die Karte des Beitrags. Kein Verlauf, keine Details,
 * kein Zwischenstand auf dieser Seite: Das Ergebnis oeffnet sich ueber "Ergebnis oeffnen".
 */

/**
 * Tickt jede halbe Minute, damit "haengt" auch ohne neue Meldung von Firestore erkannt wird --
 * genau dann will man wissen, ob es weitergeht.
 */
const TAKT_MS = 30_000;

function useJetzt(aktiv: boolean): Date {
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    if (!aktiv) return;
    const takt = setInterval(() => setJetzt(new Date()), TAKT_MS);
    return () => clearInterval(takt);
  }, [aktiv]);
  return jetzt;
}

/** Der Stand in Worten wie im Stepper der Content-Factory ("Schritt 6 von 9: Freigabe"). */
export function standSatz(phasen: readonly Phase[] | null, fertig: boolean): string | null {
  if (!phasen || phasen.length === 0) return null;
  if (fertig) return `Alle ${phasen.length} Schritte sind durchlaufen.`;
  const index = phasen.findIndex((p) => p.zustand === 'aktiv' || p.zustand === 'gescheitert');
  const phase = phasen[index];
  if (!phase) return null;
  const titel = `${phase.schritt.titel}${phase.ueberarbeitung ? ' (Überarbeitung)' : ''}`;
  const satz = `Schritt ${index + 1} von ${phasen.length}: ${titel}`;
  if (phase.zustand !== 'gescheitert') return satz;
  return `${satz}, ${phase.abgebrochen ? 'abgebrochen' : 'gescheitert'}`;
}

interface Ziel {
  text: string;
  pfad: string;
}

/**
 * Der eine Weg an der Karte, wie `beitragsZiel` der Content-Factory: wartet ein Mensch, "Jetzt
 * pruefen"; ist der Lauf durch, "Ergebnis oeffnen". Laeuft er noch, gibt es nichts zu tun --
 * ein Link ins Leere waere schlimmer als keiner.
 */
function zielVon(auftrag: Auftrag): Ziel | null {
  // Eine Ratgeber-Analyse mit Seitenliste traegt ihre Wege selbst (je Seite, siehe AnalyseSeitenListe).
  if (auftrag.typ === 'analyse' && auftrag.analyseBefund) return null;
  const id = encodeURIComponent(auftrag.runId);
  if (auftrag.status === 'wartet_auf_entscheidung' && auftrag.befund?.auditId) {
    return { text: 'Jetzt prüfen', pfad: `/auftraege/${id}/entscheidung` };
  }
  if (hatFreigabeSeite(auftrag)) return { text: 'Jetzt prüfen', pfad: `/auftraege/${id}/freigabe` };
  const gruppe = gruppeVon(auftrag.status);
  if (gruppe === 'fertig') return { text: 'Ergebnis öffnen', pfad: `/auftraege/${id}/ergebnis` };
  // Ein gescheiterter Auftrag ohne Teilergebnis braucht einen Ausweg, sonst endet die Seite in
  // einer Fehlermeldung ohne naechsten Schritt (Rueckmeldung 2026-10-13).
  if (gruppe === 'fehler') return { text: 'Neu anlegen', pfad: `/neu/${auftrag.typ}` };
  return null;
}

/**
 * Der Weg an einem FREMDEN Auftrag (Gesamtansicht der Rolle `admin`): nur zum Lesen. Statt "Jetzt
 * pruefen" der Zwischenstand auf der Ergebnisseite, die vor dem Abschluss genau den zeigt; statt
 * "Neu anlegen" nichts, denn ein fremder gescheiterter Auftrag ist nicht der eigene Ausweg.
 */
function leseZielVon(auftrag: Auftrag): Ziel | null {
  if (auftrag.typ === 'analyse' && auftrag.analyseBefund) return null;
  const id = encodeURIComponent(auftrag.runId);
  if (auftrag.status === 'wartet_auf_entscheidung' && auftrag.befund?.auditId) {
    return { text: 'Befund ansehen', pfad: `/auftraege/${id}/entscheidung` };
  }
  const gruppe = gruppeVon(auftrag.status);
  if (gruppe === 'fertig') return { text: 'Ergebnis öffnen', pfad: `/auftraege/${id}/ergebnis` };
  if (gruppe === 'wartet') {
    return { text: 'Zwischenstand ansehen', pfad: `/auftraege/${id}/ergebnis` };
  }
  return null;
}

/** "Deutsch · Evergreen Content" wie "Gründl · Deutsch" unter dem Kartentitel der Content-Factory. */
/**
 * Rueckweg eines Kind-Auftrags. Eine erzwungene volle Analyse stammt aus einem Auftrag ohne
 * Optimierungsbedarf: beim PDP aus dem PDP-Auftrag, beim Ratgeber (Kind `typ: 'analyse'`) aus der
 * Analyse, deren gepruefte Seite es war.
 */
function ruecklinkText(auftrag: Auftrag): string {
  if (!auftrag.bedarfErzwingen) return 'zur Seitenliste';
  return auftrag.typ === 'analyse' ? 'zur Ursprungsanalyse' : 'zum Ursprungsauftrag';
}

function kartenMeta(auftrag: Auftrag): string | null {
  const briefing = auftrag.briefing;
  if (!briefing) return null;
  const felder = FELDSAETZE[auftrag.typ];
  const text = (name: 'sprache' | 'contentTyp') => {
    const wert = briefing[name];
    if (!wert) return null;
    return (
      felder.find((f) => f.name === name)?.optionen?.find((o) => o.wert === wert)?.text ?? null
    );
  };
  const teile = [text('sprache'), text('contentTyp')].filter((t): t is string => t !== null);
  return teile.length > 0 ? teile.join(' · ') : null;
}

/**
 * Die bewegte Figur der Content-Factory passend zur Arbeit: Bilder malt die Staffelei, die
 * Finalisierung hakt ein Stift ab, alles andere tippt die Figur am Schreibtisch.
 */
function LaufFigur({ auftrag }: { auftrag: Auftrag }) {
  if (auftrag.typ === 'bilder') return <ImageSpinner />;
  if (auftrag.status === 'finalisierung') return <FinalizationSpinner />;
  return <RevisionSpinner />;
}

/** Ehrliche Zeitangabe wie in der Content-Factory: wie lange schon, und was ueblich ist. */
export function laufzeitSatz(auftrag: Auftrag, jetzt: Date): string {
  // Nach einer Entscheidung zaehlt die Zeit seit dem Anlegen auch das Warten auf den Menschen
  // mit und wirkte zu lang; "bis zur Freigabe" stimmte dann auch nicht mehr (Rueckmeldung
  // 2026-10-13). Gesagt wird, was jetzt noch folgt.
  if (auftrag.status === 'finalisierung') {
    return 'Ihre Freigabe ist angekommen, jetzt folgt nur noch die Finalisierung. Sie dauert meist nur wenige Minuten.';
  }
  if (auftrag.status === 'in_ueberarbeitung') {
    return 'Ihre Rückmeldung wird eingearbeitet. Sobald die überarbeitete Fassung bereitliegt, erhalten Sie eine E-Mail.';
  }
  const minuten = Math.max(
    0,
    Math.floor((jetzt.getTime() - new Date(auftrag.angelegtAm).getTime()) / 60_000),
  );
  const seit =
    minuten < 1
      ? 'Der Auftrag ist gerade gestartet.'
      : `Der Auftrag läuft seit ${minuten === 1 ? 'einer Minute' : `${minuten} Minuten`}.`;
  const typ: TypBeschreibung = AUFTRAGSTYPEN[auftrag.typ];
  return `${seit} Üblich: ${typ.dauer}.`;
}

/**
 * Was ein Mensch braucht, wenn ein Auftrag stehen bleibt (Rueckmeldung 2026-10-13: "laesst einen
 * ratlos zurueck"): was los ist, dass es nicht an ihm liegt, dass nichts verloren ist, und genau
 * ein naechster Schritt mit der Auftragsnummer, die die Administration zum Nachsehen braucht.
 */
function HaengtHinweis({ auftrag, jetzt }: { auftrag: Auftrag; jetzt: Date }) {
  const schritt =
    aktuellerSchritt(auftrag.typ, auftrag.status)?.titel ?? STATUS_TEXT[auftrag.status];
  const zuletzt = new Date(auftrag.aktualisiertAm);
  const heute = zuletzt.toDateString() === jetzt.toDateString();
  const seit = heute
    ? `seit ${alsUhrzeit(auftrag.aktualisiertAm)} Uhr`
    : `seit dem ${alsDatum(auftrag.aktualisiertAm)}`;
  // Die Kennung ist eine UUID -- vorlesen oder abtippen ist keine Hilfe. Kopiert wird, was die
  // Administration zum Nachsehen braucht, samt Adresse dieser Seite.
  const [kopiert, setKopiert] = useState<'nein' | 'ja' | 'fehler'>('nein');
  function kopieren() {
    const angaben = [
      `Auftrag ${auftrag.runId} (${AUFTRAGSTYPEN[auftrag.typ].text})`,
      `Angelegt am ${alsDatum(auftrag.angelegtAm)} um ${alsUhrzeit(auftrag.angelegtAm)} Uhr`,
      `Steht ${seit} auf „${schritt}“`,
      window.location.href,
    ].join('\n');
    // `clipboard` fehlt ausserhalb eines sicheren Kontexts; dann bleibt der Hinweis auf die Adresse.
    const ablage = navigator.clipboard as Clipboard | undefined;
    if (!ablage) {
      setKopiert('fehler');
      return;
    }
    ablage.writeText(angaben).then(
      () => setKopiert('ja'),
      () => setKopiert('fehler'),
    );
  }
  return (
    <div className={`eds-alert eds-alert--warning ${stile.haengt}`} role="alert">
      <span className={stile.haengtSymbol}>
        <LinienSymbol name="alert" groesse={20} />
      </span>
      <div className={stile.haengtText}>
        <p className="eds-alert__title">Ihr Auftrag kommt gerade nicht weiter</p>
        <p>
          Die Content Engine hat {seit} keinen neuen Stand gemeldet, zuletzt „{schritt}“. An Ihren
          Eingaben liegt es nicht, und alles, was Sie bisher angegeben und entschieden haben, bleibt
          gespeichert.
        </p>
        <p>
          Bitte melden Sie sich bei der Administration und schicken Sie ihr die Angaben zu diesem
          Auftrag mit. Mit dem Knopf unten kopieren Sie alles Nötige in einem Schritt, Sie müssen
          nichts neu eingeben.
        </p>
        <div className={stile.haengtAktion}>
          <button
            type="button"
            className="eds-button eds-button--secondary eds-button--sm"
            onClick={kopieren}
          >
            <LinienSymbol name={kopiert === 'ja' ? 'check' : 'file'} />
            {kopiert === 'ja' ? 'Kopiert' : 'Angaben kopieren'}
          </button>
          <span className={stile.auftragsnummer}>Auftragsnummer: {auftrag.runId}</span>
        </div>
        {kopiert === 'fehler' && (
          <p role="status">
            Das Kopieren hat nicht geklappt. Bitte schicken Sie der Administration die Adresse
            dieser Seite, sie enthält die Auftragsnummer.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Die Kennung, unter der auch n8n den Lauf fuehrt (`run_id` in jedem Aufruf und jeder
 * Statusmeldung). Nennt ein Nutzer sie, findet das Team die Executions ohne Raten
 * (Rueckmeldung 2026-10-14). Zum Kopieren ein Knopf, weil eine UUID niemand abtippt.
 */
function AuftragsId({ runId }: { runId: string }) {
  const [kopiert, setKopiert] = useState(false);
  function kopieren() {
    // `clipboard` fehlt ausserhalb eines sicheren Kontexts; dann bleibt die markierbare Kennung.
    const ablage = navigator.clipboard as Clipboard | undefined;
    ablage?.writeText(runId).then(
      () => setKopiert(true),
      () => setKopiert(false),
    );
  }
  return (
    <p className={stile.auftragsId}>
      Auftrags-ID: <span className={stile.kennung}>{runId}</span>
      <button
        type="button"
        className={stile.aktualisieren}
        onClick={kopieren}
        aria-label={kopiert ? 'Auftrags-ID kopiert' : 'Auftrags-ID kopieren'}
        title={kopiert ? 'Kopiert' : 'Kopieren'}
      >
        <LinienSymbol name={kopiert ? 'check' : 'file'} groesse={14} />
      </button>
    </p>
  );
}

function BeitragsKarte({
  auftrag,
  jetzt,
  quelle,
  fremd,
  starteVolleAnalyse,
}: {
  auftrag: Auftrag;
  jetzt: Date;
  quelle: AuftragsQuelle;
  fremd: boolean;
  starteVolleAnalyse: StarteVolleAnalyse;
}) {
  const typ = AUFTRAGSTYPEN[auftrag.typ];
  const gruppe = gruppeVon(auftrag.status);
  const phasen = phasenZustaende(auftrag.typ, auftrag.status, auftrag.verlauf);
  const satz = standSatz(phasen, auftrag.status === 'fertig');
  const haengt = istHaengend(auftrag, jetzt);
  const meta = kartenMeta(auftrag);
  const ziel = fremd ? leseZielVon(auftrag) : zielVon(auftrag);

  return (
    <section className={stile.karte} aria-labelledby="beitrag-titel">
      <div className={stile.kartenKopf}>
        <h2 id="beitrag-titel" className={stile.typ}>
          <span className={stile.typSymbol}>
            <LinienSymbol name={AUFTRAGSTYPEN[auftrag.typ].symbol} groesse={18} />
          </span>
          {typ.text}
        </h2>
        <Statusmarke ton={TON_JE_GRUPPE[gruppe]}>
          {statusText(auftrag.status, auftrag.typ)}
        </Statusmarke>
      </div>

      {meta && <p className={stile.kartenMeta}>{meta}</p>}
      {/* Eine volle Analyse (`bedarfErzwingen`, Spec 2026-11-03-kein-bedarf) stammt nicht aus
          einer Seitenliste, sondern aus einem Auftrag ohne Optimierungsbedarf. */}
      {auftrag.ausAnalyseRunId && (
        <p className={stile.kartenMeta}>
          {auftrag.bedarfErzwingen
            ? 'Volle Analyse trotz „Kein Optimierungsbedarf“:'
            : 'Aus einer Ratgeber-Analyse:'}{' '}
          <Link to={`/auftraege/${encodeURIComponent(auftrag.ausAnalyseRunId)}`}>
            {ruecklinkText(auftrag)}
          </Link>
        </p>
      )}
      {auftrag.typ === 'ratgeber' &&
        auftrag.pdpEntscheidung?.hinweisVerworfen &&
        auftrag.pdpEntscheidung.zusatzhinweis && (
          <div className="eds-alert eds-alert--warning" role="status">
            <p>{HINWEIS_VERWORFEN_TEXT}</p>
          </div>
        )}

      <div className={stile.stepper}>
        {satz && <p className={stile.satz}>{satz}</p>}
        {phasen && (
          <Phasenleiste
            phasen={phasen}
            wartet={gruppe === 'wartet'}
            bewegt={gruppe === 'laeuft' && !haengt}
          />
        )}
      </div>

      {haengt && <HaengtHinweis auftrag={auftrag} jetzt={jetzt} />}

      {gruppe === 'fehler' && istVerworfen(auftrag) && (
        <p className={stile.hinweis} role="status">
          {auftrag.fehler ?? 'Sie haben die Optimierung verworfen.'}
        </p>
      )}
      {gruppe === 'fehler' && !istVerworfen(auftrag) && (
        <p className={stile.technischerHinweis} role="alert">
          {auftrag.status === 'abgebrochen'
            ? 'Der Auftrag wurde abgebrochen.'
            : 'Der Auftrag ist gescheitert.'}{' '}
          {auftrag.fehler ?? 'Es liegt keine nähere Angabe vor.'}
        </p>
      )}

      <BefundKarte auftrag={auftrag} />
      <AnalyseSeitenListe auftrag={auftrag} quelle={quelle} starte={starteVolleAnalyse} />

      {ziel && (
        <div className={stile.aktionen}>
          <Link className="eds-button eds-button--primary eds-button--sm" to={ziel.pfad}>
            {ziel.text}
          </Link>
        </div>
      )}
    </section>
  );
}

function Fortschritt({
  quelle,
  runId,
  aktualisieren,
  starteVolleAnalyse,
}: {
  quelle: AuftragsQuelle;
  runId: string;
  aktualisieren: () => void;
  starteVolleAnalyse: StarteVolleAnalyse;
}) {
  const zustand = useAuftrag(quelle, runId);
  const uid = useAngemeldeterNutzer()?.uid ?? null;
  const auftragOderNull = zustand.art === 'daten' ? zustand.auftrag : null;
  const laeuftNoch =
    auftragOderNull !== null &&
    gruppeVon(auftragOderNull.status) !== 'fertig' &&
    gruppeVon(auftragOderNull.status) !== 'fehler';
  const jetzt = useJetzt(laeuftNoch);

  // "Zuletzt nachgesehen" wie in der Content-Factory: wann zuletzt ein Stand ankam, NICHT wann
  // sich der Auftrag zuletzt bewegt hat. Firestore meldet jeden Stand von selbst.
  const [nachgesehen, setNachgesehen] = useState<Date | null>(null);
  useEffect(() => {
    if (zustand.art === 'daten') setNachgesehen(new Date());
  }, [zustand]);

  const pfad = [{ text: 'Übersicht', ziel: '/' }];

  if (zustand.art === 'laedt') {
    return (
      <>
        <Seitenkopf titel="Auftrag" pfad={pfad} />
        <LoadingState text="Auftrag wird geladen …" />
      </>
    );
  }
  if (zustand.art === 'fehler') {
    return (
      <>
        <Seitenkopf titel="Auftrag" pfad={pfad} />
        <ErrorState text={zustand.text} onWiederholen={zustand.wiederholen} />
      </>
    );
  }

  // Ab hier sieht die GANZE Seite den Anzeigestatus (`zurAnzeige`, uebergabe.ts): nach einer
  // Freigabe `finalisierung` statt des von n8n gemeldeten `in_ueberarbeitung`. Kopf, Figur,
  // Zeitangabe, Schrittleiste und Status lesen dasselbe Objekt (Codex-Gate PR #70).
  const auftrag = zustand.auftrag ? zurAnzeige(zustand.auftrag) : null;
  if (!auftrag) {
    return (
      <>
        <Seitenkopf titel="Auftrag nicht gefunden" pfad={pfad} />
        <div className={stile.seite}>
          <p>
            Diesen Auftrag gibt es nicht, oder er gehört zu einem anderen Konto. Prüfen Sie den
            Verweis, über den Sie hierher gekommen sind.
          </p>
          <Link className="eds-button eds-button--primary" to="/">
            Zur Übersicht
          </Link>
        </div>
      </>
    );
  }

  const gruppe = gruppeVon(auftrag.status);
  const gestoert = gruppe === 'fehler';
  // Ein haengender Auftrag ist nicht "In Ordnung", und "laeuft im Hintergrund weiter" mit
  // "dauert meist nur wenige Minuten" waere neben dem Hinweis eine falsche Auskunft.
  const haengt = istHaengend(auftrag, jetzt);
  const fremd = istFremd(auftrag, uid);

  return (
    <>
      <Seitenkopf
        titel={fremd ? 'Fortschritt des Auftrags' : 'Fortschritt Ihres Auftrags'}
        pfad={pfad}
      />
      <div className={stile.seite}>
        <header className={stile.kopf}>
          <div className={stile.kopfZeile}>
            <p className={stile.meta}>
              Angelegt am {alsDatum(auftrag.angelegtAm)} · {AUFTRAGSTYPEN[auftrag.typ].text}
              {auftrag.runde !== undefined && <> · Freigaberunde {auftrag.runde}</>}
            </p>
            {istVerworfen(auftrag) ? (
              <Statusmarke ton="neutral">Verworfen</Statusmarke>
            ) : (
              <Statusmarke ton={gestoert ? 'fehler' : haengt ? 'warnung' : 'erfolg'}>
                {gestoert ? 'Gestört' : haengt ? 'Verzögert' : 'In Ordnung'}
              </Statusmarke>
            )}
          </div>
          <div className={stile.standZeile}>
            <AuftragsId runId={auftrag.runId} />
            <p className={stile.stand}>
              Zuletzt nachgesehen:{' '}
              <time dateTime={nachgesehen?.toISOString()} data-testid="nachgesehen">
                {nachgesehen ? alsUhrzeit(nachgesehen.toISOString()) : '–'} Uhr
              </time>
            </p>
            <button
              type="button"
              className={stile.aktualisieren}
              onClick={aktualisieren}
              aria-label="Jetzt aktualisieren"
            >
              <LinienSymbol name="refresh" groesse={16} />
            </button>
          </div>
        </header>

        {fremd && (
          <div className="eds-alert eds-alert--info" role="status">
            <p>
              Angelegt von {angelegtVon(auftrag, uid)}. {NUR_ANSICHT_TEXT}
            </p>
          </div>
        )}

        {/* Nur solange wirklich etwas laeuft: an einem wartenden oder fertigen Auftrag waere der
            Satz eine falsche Auskunft (Content-Factory #1438). */}
        {gruppe === 'laeuft' && !haengt && (
          <section className={stile.hintergrund} aria-label="Verarbeitung im Hintergrund">
            <span className={stile.figur}>
              <LaufFigur auftrag={auftrag} />
            </span>
            <div role="status" className={stile.hintergrundText}>
              <h2>Der Auftrag läuft im Hintergrund weiter.</h2>
              <p>
                {laufzeitSatz(auftrag, jetzt)} Sie können diese Seite verlassen oder schließen.
                Ihren Auftrag finden Sie später in der Übersicht wieder.
              </p>
            </div>
            <Link to="/" className="eds-button eds-button--secondary">
              Zur Übersicht
            </Link>
          </section>
        )}

        <div className={stile.spalten}>
          <BriefingKarte auftrag={auftrag} />
          <div className={stile.beitraege}>
            <BeitragsKarte
              auftrag={auftrag}
              jetzt={jetzt}
              quelle={quelle}
              fremd={fremd}
              starteVolleAnalyse={starteVolleAnalyse}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export function RunDetailPage({
  quelle,
  starteVolleAnalyse,
}: {
  quelle: AuftragsQuelle;
  /** "Dennoch volle Analyse durchführen" je gepruefter Seite einer Ratgeber-Analyse. */
  starteVolleAnalyse: StarteVolleAnalyse;
}) {
  const { runId = '' } = useParams();
  const bestaetigung = useBestaetigung();
  // Aktualisieren meldet den Listener neu an: Firestore liefert dann den Stand vom Server neu,
  // statt nur auf die naechste Aenderung zu warten.
  const [abruf, setAbruf] = useState(0);
  return (
    <>
      <Fortschritt
        key={`${runId}-${abruf}`}
        quelle={quelle}
        runId={runId}
        aktualisieren={() => setAbruf((n) => n + 1)}
        starteVolleAnalyse={starteVolleAnalyse}
      />
      <HinweisBereich hinweis={bestaetigung.hinweis} onSchliessen={bestaetigung.schliessen} />
    </>
  );
}

/**
 * Kurze Bestaetigung wie der Toast der Content-Factory, wenn man von "Starten" oder "Freigeben"
 * hierher kommt. Die Nachricht reist im Navigationszustand mit und wird sofort daraus geloescht,
 * damit ein Neuladen sie nicht noch einmal zeigt.
 */
function useBestaetigung() {
  const ort = useLocation();
  const navigiere = useNavigate();
  const text = (ort.state as { bestaetigung?: unknown } | null)?.bestaetigung;
  const [hinweis, setHinweis] = useState<HinweisDaten | null>(() =>
    typeof text === 'string' ? { id: 1, text } : null,
  );
  useEffect(() => {
    if (typeof text === 'string') navigiere(ort.pathname, { replace: true, state: null });
  }, [text, navigiere, ort.pathname]);
  return { hinweis, schliessen: () => setHinweis(null) };
}
